import {
	type BulkGameOutcome,
	bulkGameOutcomeSchema,
	gameDetailSchema,
	gameSchema,
} from "../schemas/game";
import type { ModerationStatus } from "../types/content";
import type { GameStatus } from "../types/core";
import { fetchApi, fetchVoid, paginatedSchema, toSearchParams } from "./internal";

export type SortOrder = "asc" | "desc";

/** Sort fields `GET /admin/games` whitelists; anything else is rejected server-side. */
export const GAME_SORT_FIELDS = [
	"created_at",
	"start_time",
	"title",
	"status",
	"max_players",
] as const;

export type GameSortField = (typeof GAME_SORT_FIELDS)[number];

/** Narrows a free-form table sort column to one the server accepts. */
export const isGameSortField = (value: string | undefined): value is GameSortField =>
	value !== undefined && (GAME_SORT_FIELDS as readonly string[]).includes(value);

export interface ListParams {
	limit?: number;
	offset?: number;
	cursor?: string;
	q?: string;
	sort_by?: string;
	order?: SortOrder;
}

/** `ListParams` plus the two filters only `GET /admin/games` understands. */
export interface GameListParams extends ListParams {
	status?: GameStatus;
	moderation_status?: ModerationStatus;
	sort_by?: GameSortField;
}

/**
 * Body of `PATCH /admin/games?ids=…` — bulk cancel and/or the website
 * kill-switch. The server rejects any status other than `cancelled`.
 */
export interface GameBulkPatch {
	hidden_from_web?: boolean;
	status?: "cancelled";
}

const EMPTY_OUTCOME: BulkGameOutcome = { failed: [], succeeded: [] };

export const games = {
	delete: (id: string) => fetchVoid(`/admin/games/${id}`, { method: "DELETE" }),
	/** Hard-deletes the rows — participants, invites, and chat go with them. */
	deleteMany: (ids: string[]): Promise<BulkGameOutcome> =>
		ids.length === 0
			? Promise.resolve(EMPTY_OUTCOME)
			: fetchApi(`/admin/games?${toSearchParams({ ids: ids.join(",") })}`, bulkGameOutcomeSchema, {
					method: "DELETE",
				}),
	get: (id: string) => fetchApi(`/admin/games/${id}`, gameDetailSchema),
	list: ({
		limit = 20,
		offset = 0,
		cursor,
		q,
		sort_by,
		order,
		status,
		moderation_status,
	}: GameListParams = {}) => {
		const qs = toSearchParams({
			cursor,
			limit,
			moderation_status,
			offset,
			order,
			q,
			sort_by,
			status,
		});
		return fetchApi(`/admin/games?${qs}`, paginatedSchema(gameSchema));
	},
	patchMany: (ids: string[], patch: GameBulkPatch): Promise<BulkGameOutcome> =>
		ids.length === 0
			? Promise.resolve(EMPTY_OUTCOME)
			: fetchApi(`/admin/games?${toSearchParams({ ids: ids.join(",") })}`, bulkGameOutcomeSchema, {
					body: JSON.stringify(patch),
					method: "PATCH",
				}),
	update: (id: string, data: unknown) =>
		fetchApi(`/admin/games/${id}`, gameSchema, {
			body: JSON.stringify(data),
			method: "PUT",
		}),
};
